/**
 * Auth bridge — shared single source of truth for getting a fresh JWT.
 *
 * Background:
 *   Clerk session tokens have a SHORT TTL (≈60 s by default). Reading the
 *   token from localStorage and reusing it across multiple API calls means
 *   ~half of those calls hit the worker with an expired token → 401.
 *
 *   The fix is to call Clerk's `getToken()` immediately before every API
 *   call. But the API helpers are module-level functions, not React
 *   components, so they can't `useAuth()` hook the way the rest of the app
 *   does. This module provides the bridge: AuthContext registers a
 *   `tokenGetter` here on mount, and the API helpers call it when they
 *   need a current token.
 *
 *   Falls back to the localStorage value if no getter is registered yet
 *   (e.g. very first call during initial mount).
 */

type TokenGetter = (opts?: { skipCache?: boolean }) => Promise<string | null>;

let tokenGetter: TokenGetter | null = null;

export function registerTokenGetter(fn: TokenGetter | null): void {
  tokenGetter = fn;
}

export async function getFreshToken(): Promise<string | null> {
  if (tokenGetter) {
    try {
      const t = await tokenGetter();
      if (t) {
        // Mirror to localStorage so non-bridge consumers keep working.
        try { localStorage.setItem('clerk_token', t); } catch { /* ignore */ }
        return t;
      }
    } catch {
      // Fall through to localStorage cache
    }
  }
  return localStorage.getItem('clerk_token');
}
